const customElementsName = 'scrolling-content';

!window.customElements.get(customElementsName) &&
    window.customElements.define(customElementsName, class extends HTMLElement {
        constructor() {
            super();
            this.resizeObserver = null;
            this.resizeCleanup = null;
            this.boundHandleSectionLoad = null;
            this.boundHandleSectionUnload = null;
            this.animationFrameId = null;
            this.scrollSpeed = 1;
            this.autoplayTimer = null;
            this.currentIndex = 0;
            this.isAnimating = false;
            this.autoplayRunning = false;
        }



        connectedCallback() {
            // Calculate element height when connected (with multiple attempts to ensure layout is ready)
            requestAnimationFrame(() => {
                this.calculateHeight();
                // Recalculate after a short delay to account for any async content
                setTimeout(() => this.calculateHeight(), 100);
            });

            // Delay initialization to ensure DOM is ready
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
            } else {
                requestAnimationFrame(() => this.init());
            }

            // Handle Shopify theme editor section reload
            const sectionRoot = this.closest('[id^="shopify-section-"]');
            if (sectionRoot) {
                this.sectionId = sectionRoot.id;
                this.boundHandleSectionLoad = this.handleSectionLoad.bind(this);
                this.boundHandleSectionUnload = this.handleSectionUnload.bind(this);
                document.addEventListener('shopify:section:load', this.boundHandleSectionLoad);
                document.addEventListener('shopify:section:unload', this.boundHandleSectionUnload);
            }
        }

        calculateHeight() {
            const track = this.querySelector('.marquee__track');
            if (!track) {
                return;
            }

            const items = Array.from(track.querySelectorAll('.block-item'));
            if (!items.length) {
                return;
            }

            // Calculate max height only (width is always 100% viewport)
            let maxHeight = 0;

            items.forEach((item) => {
                const rect = item.getBoundingClientRect();
                maxHeight = Math.max(maxHeight, rect.height);
            });

            // Set CSS variable for height
            this.style.setProperty('--marquee-item-height', `${Math.ceil(maxHeight)}px`);
            this.classList.add('initialized');
        }

        disconnectedCallback() {
            this.cleanup();
            if (this.sectionId && this.boundHandleSectionLoad && this.boundHandleSectionUnload) {
                document.removeEventListener('shopify:section:load', this.boundHandleSectionLoad);
                document.removeEventListener('shopify:section:unload', this.boundHandleSectionUnload);
                this.boundHandleSectionLoad = null;
                this.boundHandleSectionUnload = null;
            }
        }

        handleSectionLoad(event) {
            if (event?.target?.id === this.sectionId) {
                requestAnimationFrame(() => this.init());
            }
        }

        handleSectionUnload(event) {
            if (event?.target?.id === this.sectionId) {
                this.cleanup();
            }
        }

        init() {
            // Recalculate height during init
            this.calculateHeight();

            const track = this.querySelector('.marquee__track');
            if (!track) {
                return;
            }

            // Remove any existing clones before reinitializing
            // Clones are marked with data-is-clone="true"
            const allItems = Array.from(track.querySelectorAll('.block-item'));

            // Remove all clones (marked with data-is-clone)
            allItems.forEach((item) => {
                if (item.dataset.isClone === 'true') {
                    item.remove();
                }
            });

            // Get fresh list of original items only
            const items = Array.from(track.querySelectorAll('.block-item'));
            if (!items.length) {
                return;
            }

            // Get scroll speed from data attribute
            this.scrollSpeed = parseFloat(this.dataset.speed) || 1;
            this.duration = parseFloat(this.dataset.duration) || 500;

            const direction = this.dataset.direction || 'horizontal';
            if (direction === 'horizontal') {
                this.setupMarquee(track, items, {
                    axis: 'X',
                    dimension: 'width',
                    flexDirection: 'row',
                    isReverse: false,
                    calculateSize: () => this.getBoundingClientRect().width,
                    updateItemSize: (item, size) => {
                        item.style.width = `${size}px`;
                    },
                    updateContainer: () => {
                        // No special container setup for horizontal
                    },
                    handleResize: (allItems, size) => {
                        allItems.forEach((item) => {
                            item.style.width = `${size}px`;
                        });
                    }
                });
            } else if (direction === 'horizontal-reverse') {
                this.setupMarquee(track, items, {
                    axis: 'X',
                    dimension: 'width',
                    flexDirection: 'row',
                    isReverse: true,
                    calculateSize: () => this.getBoundingClientRect().width,
                    updateItemSize: (item, size) => {
                        item.style.width = `${size}px`;
                    },
                    updateContainer: () => {
                        // No special container setup for horizontal
                    },
                    handleResize: (allItems, size) => {
                        allItems.forEach((item) => {
                            item.style.width = `${size}px`;
                        });
                    }
                });
            } else if (direction === 'vertical') {
                this.setupMarquee(track, items, {
                    axis: 'Y',
                    dimension: 'height',
                    flexDirection: 'column',
                    calculateSize: () => {
                        // Reset any existing height styles to get natural height
                        items.forEach((item) => {
                            item.style.height = '';
                            item.style.minHeight = '';
                        });
                        void track.offsetHeight;

                        // Calculate max height from all items
                        let itemHeight = 0;
                        items.forEach((item) => {
                            const rect = item.getBoundingClientRect();
                            itemHeight = Math.max(itemHeight, rect.height);
                        });

                        if (itemHeight === 0) {
                            itemHeight = 40; // fallback minimum height
                        }

                        return itemHeight;
                    },
                    updateItemSize: (item, size) => {
                        item.style.height = `${size}px`;
                        item.style.minHeight = `${size}px`;
                    },
                    updateContainer: (size) => {
                        this.style.height = `${size}px`;
                        this.style.overflow = 'hidden';
                        track.style.overflow = 'hidden';
                        track.style.height = '';
                    },
                    handleResize: (allItems, size, originalItems) => {
                        // Recalculate item height from content
                        originalItems.forEach((item) => {
                            item.style.height = '';
                            item.style.minHeight = '';
                        });
                        void track.offsetHeight;

                        let newItemHeight = 0;
                        originalItems.forEach((item) => {
                            const rect = item.getBoundingClientRect();
                            newItemHeight = Math.max(newItemHeight, rect.height);
                        });

                        if (newItemHeight === 0) {
                            newItemHeight = size; // Keep previous height if calculation fails
                        }

                        this.style.height = `${newItemHeight}px`;
                        track.style.height = '';

                        allItems.forEach((item) => {
                            item.style.height = `${newItemHeight}px`;
                            item.style.minHeight = `${newItemHeight}px`;
                        });

                        return newItemHeight;
                    }
                });
            }
        }

        setupMarquee(track, items, config) {
            const originalItemsCount = items.length;
            const { axis, flexDirection, isReverse = false, calculateSize, updateItemSize, updateContainer, handleResize } = config;

            // Store original count in track for cleanup
            track.dataset.originalCount = originalItemsCount.toString();

            // Calculate initial size
            let itemSize = calculateSize();

            // Update container if needed
            updateContainer(itemSize);

            // Only clone if we have more than 1 item (no need for infinite scroll with 1 item)
            if (originalItemsCount > 1) {
                // Clone items for infinite scroll effect
                items.forEach((item) => {
                    updateItemSize(item, itemSize);
                    item.style.flexShrink = '0';

                    // Clone each item and append to track for seamless loop
                    const clone = item.cloneNode(true);
                    updateItemSize(clone, itemSize);
                    clone.style.flexShrink = '0';
                    // Mark clone with data attribute for easier identification
                    clone.dataset.isClone = 'true';
                    track.appendChild(clone);
                });
            } else {
                // Single item - just set size
                items.forEach((item) => {
                    updateItemSize(item, itemSize);
                    item.style.flexShrink = '0';
                });
            }

            // Get all items including clones
            const allItems = Array.from(track.querySelectorAll('.block-item'));

            // Set track to use flexbox
            track.style.display = 'flex';
            if (flexDirection !== 'row') {
                track.style.flexDirection = flexDirection;
            }
            track.style.transition = 'none';

            // Initialize index and position based on direction
            this.currentIndex = isReverse ? originalItemsCount - 1 : 0;
            const initialPosition = isReverse ? -itemSize * this.currentIndex : 0;
            track.style.transform = `translate${axis}(${initialPosition}px)`;
            const transitionDuration = this.duration; // Fixed transition duration in ms (smooth slide effect)
            const autoplayTimeout = (this.scrollSpeed || 1) * 1000; // Time between slides (configurable)

            // Function to move to next slide (or previous for reverse)
            const slideNext = () => {
                if (this.isAnimating || !this.autoplayRunning) {
                    return;
                }

                this.isAnimating = true;

                if (isReverse) {
                    // Reverse direction: move backwards
                    this.currentIndex--;

                    // Check if we've reached the beginning (before first item)
                    if (originalItemsCount > 1 && this.currentIndex < 0) {
                        // Calculate target position for the last clone (clone of last original item)
                        // Clones are appended after originals, so last clone is at index (originalItemsCount * 2 - 1)
                        // Position = -(originalItemsCount * 2 - 1) * itemSize
                        const lastCloneIndex = originalItemsCount * 2 - 1;
                        const targetPosition = -lastCloneIndex * itemSize;

                        // Set transition for smooth scroll to clone
                        track.style.transition = `transform ${transitionDuration}ms ease-in-out`;
                        track.style.transform = `translate${axis}(${targetPosition}px)`;

                        // After transition completes, reset invisibly to original position
                        setTimeout(() => {
                            // Reset to last original item position without transition (invisible to user)
                            track.style.transition = 'none';
                            this.currentIndex = originalItemsCount - 1;
                            track.style.transform = `translate${axis}(${-itemSize * this.currentIndex}px)`;

                            // Force reflow to ensure transition is reset
                            void (axis === 'X' ? track.offsetWidth : track.offsetHeight);

                            this.isAnimating = false;
                        }, transitionDuration);
                    } else {
                        // Normal slide to previous item (originalItemsCount-1, ..., 1, 0)
                        const targetPosition = -itemSize * this.currentIndex;

                        // Set transition for smooth scroll
                        track.style.transition = `transform ${transitionDuration}ms ease-in-out`;
                        track.style.transform = `translate${axis}(${targetPosition}px)`;

                        // Reset animation flag after transition completes
                        setTimeout(() => {
                            this.isAnimating = false;
                        }, transitionDuration);
                    }
                } else {
                    // Forward direction: move forwards (original logic)
                    this.currentIndex++;

                    // Check if we've reached the cloned items (end of original items)
                    // When we reach the clones, we'll reset invisibly to the original items
                    // Only do this if we have more than 1 item
                    if (originalItemsCount > 1 && this.currentIndex >= originalItemsCount) {
                        // Calculate target position for the clone (this is the first clone, identical to first original item)
                        const targetPosition = -itemSize * this.currentIndex;

                        // Set transition for smooth scroll to clone
                        track.style.transition = `transform ${transitionDuration}ms ease-in-out`;
                        track.style.transform = `translate${axis}(${targetPosition}px)`;

                        // After transition completes, reset invisibly to original position
                        setTimeout(() => {
                            // Reset to start position without transition (invisible to user)
                            // This happens when we're showing the clone of the first item
                            track.style.transition = 'none';
                            this.currentIndex = 0;
                            track.style.transform = `translate${axis}(0)`;

                            // Force reflow to ensure transition is reset
                            void (axis === 'X' ? track.offsetWidth : track.offsetHeight);

                            this.isAnimating = false;
                        }, transitionDuration);
                    } else {
                        // Normal slide to next item (0, 1, 2, ..., originalItemsCount-1)
                        const targetPosition = -itemSize * this.currentIndex;

                        // Set transition for smooth scroll
                        track.style.transition = `transform ${transitionDuration}ms ease-in-out`;
                        track.style.transform = `translate${axis}(${targetPosition}px)`;

                        // Reset animation flag after transition completes
                        setTimeout(() => {
                            this.isAnimating = false;
                        }, transitionDuration);
                    }
                }
            };

            // Autoplay functions (similar to slideshow pattern)
            const setAutoplayTimer = () => {
                // Don't start autoplay if there's only 1 item
                if (originalItemsCount <= 1) {
                    return;
                }

                // Clear any existing timer
                if (this.autoplayTimer) {
                    clearInterval(this.autoplayTimer);
                }

                // Start autoplay with interval
                this.autoplayTimer = setInterval(() => {
                    slideNext();
                }, autoplayTimeout);
                this.autoplayRunning = true;
            };

            const stopAutoplayTimer = () => {
                if (this.autoplayTimer) {
                    clearInterval(this.autoplayTimer);
                    this.autoplayTimer = null;
                }
                this.autoplayRunning = false;
            };

            const startAutoplay = () => {
                if (!this.autoplayRunning) {
                    setAutoplayTimer();
                }
            };

            const stopAutoplay = () => {
                stopAutoplayTimer();
            };

            // Start autoplay
            startAutoplay();

            // Handle hover to pause/resume autoplay
            this.setupHoverPause(track, originalItemsCount, startAutoplay, stopAutoplay);

            // Handle resize
            const handleResizeEvent = () => {
                // Stop autoplay during resize
                stopAutoplay();

                // Calculate new size (may be different for horizontal vs vertical)
                let newSize;
                if (axis === 'X') {
                    newSize = this.getBoundingClientRect().width;
                    handleResize(allItems, newSize);
                } else {
                    // For vertical, handleResize recalculates and returns the new size
                    const result = handleResize(allItems, itemSize, items);
                    newSize = result !== undefined ? result : itemSize;
                }

                // Update itemSize for position calculations
                itemSize = newSize;

                // Reset position and restart (based on direction)
                this.currentIndex = isReverse ? originalItemsCount - 1 : 0;
                track.style.transition = 'none';
                const resetPosition = isReverse ? -itemSize * this.currentIndex : 0;
                track.style.transform = `translate${axis}(${resetPosition}px)`;
                this.isAnimating = false;

                // Restart autoplay after a short delay
                setTimeout(() => {
                    startAutoplay();
                }, 100);
            };

            if ('ResizeObserver' in window) {
                this.resizeObserver = new ResizeObserver(() => {
                    requestAnimationFrame(handleResizeEvent);
                });
                this.resizeObserver.observe(this);

                this.resizeCleanup = () => {
                    stopAutoplay();
                    if (this.hoverCleanup) {
                        this.hoverCleanup();
                        this.hoverCleanup = null;
                    }
                    if (this.resizeObserver) {
                        this.resizeObserver.disconnect();
                        this.resizeObserver = null;
                    }
                };
            } else {
                window.addEventListener('resize', handleResizeEvent);
                this.resizeCleanup = () => {
                    stopAutoplay();
                    if (this.hoverCleanup) {
                        this.hoverCleanup();
                        this.hoverCleanup = null;
                    }
                    window.removeEventListener('resize', handleResizeEvent);
                };
            }
        }

        // Common method to setup hover pause/resume for both horizontal and vertical
        setupHoverPause(track, itemsCount, startAutoplayFn, stopAutoplayFn) {
            // Only setup hover pause if we have more than 1 item
            if (itemsCount <= 1) {
                return;
            }

            const handleMouseEnter = () => {
                if (stopAutoplayFn) {
                    stopAutoplayFn();
                }
            };

            const handleMouseLeave = () => {
                if (startAutoplayFn) {
                    startAutoplayFn();
                }
            };

            track.addEventListener('mouseenter', handleMouseEnter);
            track.addEventListener('mouseleave', handleMouseLeave);

            // Store cleanup function
            this.hoverCleanup = () => {
                track.removeEventListener('mouseenter', handleMouseEnter);
                track.removeEventListener('mouseleave', handleMouseLeave);
            };
        }

        cleanup() {
            if (this.animationFrameId) {
                cancelAnimationFrame(this.animationFrameId);
                this.animationFrameId = null;
            }
            if (this.autoplayTimer) {
                clearInterval(this.autoplayTimer);
                this.autoplayTimer = null;
            }
            this.autoplayRunning = false;
            this.isAnimating = false;
            if (this.resizeCleanup) {
                this.resizeCleanup();
                this.resizeCleanup = null;
            }
        }
    });

